import { useNavigate, useSearch } from '@tanstack/react-router'
import { useLayoutEffect, useState, type FormEvent } from 'react'

import { defineCoreErrorResolution, resolveCoreError } from '@/shared/api/errors'
import { AppearanceMenu } from '@/shared/appearance/AppearanceMenu'
import { useAppearance } from '@/shared/appearance/AppearanceProvider'
import type { MessageId } from '@/shared/i18n/catalogue.en'
import { useI18n } from '@/shared/i18n/I18nProvider'
import { LanguageSwitcher } from '@/shared/i18n/LanguageSwitcher'
import { useLogin } from '@/shared/session/session'
import { BrandLockup } from '@/shared/branding/BrandLockup'
import { DeveloperCredit } from '@/shared/branding/DeveloperCredit'
import { PasswordInput } from '@/shared/ui/PasswordInput'
import { externalAuthorizationDestination } from '@/shared/routing/externalAuthorization'

/**
 * Every failure kind resolves to a message that says nothing about whether the
 * account exists. Maintenance is distinguished only after valid credentials;
 * throttling tells the person that waiting is the remedy.
 */
const SIGN_IN_RESOLUTION = defineCoreErrorResolution<MessageId>(
  /* Everything else is a service problem, and saying so beats inventing copy
     that implies the password was the problem. A 404, and every kind a
     credential submission cannot produce at all, lands here. */
  'login.unavailable',
  {
    byCode: { release_maintenance_access_required: 'login.maintenanceRestricted' },
    byKind: {
      'invalid-credentials': 'login.failed',
      unauthenticated: 'login.failed',
      // A disabled or unknown account must not be distinguishable here either.
      forbidden: 'login.failed',
      throttled: 'login.throttled',
    },
  },
)

export function LoginPage() {
  const { t, applyLoginLocale } = useI18n()
  const { applyLoginAppearance, releaseLoginAppearance } = useAppearance()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { telegram?: string; authorize?: string }
  const signIn = useLogin()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [fieldError, setFieldError] = useState<MessageId | null>(null)

  // The providers outlive the router. Returning from an authenticated shell
  // therefore has to restore the browser-owned login hints explicitly; doing
  // it in a layout effect keeps the previous account's presentation out of the
  // public form's first painted frame.
  useLayoutEffect(() => {
    applyLoginLocale()
    applyLoginAppearance()
    return releaseLoginAppearance
  }, [applyLoginAppearance, applyLoginLocale, releaseLoginAppearance])

  const requestError = signIn.isError ? resolveCoreError(signIn.error, SIGN_IN_RESOLUTION) : null
  const telegramErrors: Record<string, MessageId> = {
    'not-linked': 'login.telegramNotLinked', inactive: 'login.telegramInactive',
    expired: 'login.telegramExpired', invalid: 'login.telegramInvalid',
    throttled: 'login.throttled', unavailable: 'login.unavailable',
  }
  const shownError = fieldError ?? requestError ?? (search.telegram ? telegramErrors[search.telegram] ?? 'login.telegramInvalid' : null)

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (username.trim().length === 0) {
      setFieldError('login.usernameRequired')
      return
    }
    if (password.length === 0) {
      setFieldError('login.passwordRequired')
      return
    }
    setFieldError(null)

    signIn.mutate(
      { username: username.trim(), password },
      {
        onSuccess: () => {
          // The credentials never outlive the request: no storage, no URL, no
          // logging. The session lives only in the HttpOnly cookie.
          setPassword('')
          void navigate(search.authorize ? externalAuthorizationDestination(search.authorize) : { to: '/', replace: true })
        },
      },
    )
  }

  return (
    <main id="main" className="entry-screen login">
      <div className="login__card">
        <header className="login__header">
          {/* Identity on one side, the language control on the other, so the
              switcher sits toward the top inline-end here exactly as it does in
              the authenticated header. Someone who cannot read this screen must
              meet the control before the form, not below it. */}
          <div className="login__masthead">
            {/* The identity, at the one place a viewer meets the product
                first — and whose identity it is follows the appearance:
                `BrandLockup` draws the host's seal and name, or a
                white-label's own lockup, from the theme in force. The
                heading's accessible name comes with it either way. */}
            <h1>
              <BrandLockup size="lg" />
            </h1>
            {/* Both switchers, one group: language and appearance sit together
                here exactly as they do in the authenticated header, and the
                masthead's space-between keeps treating them as one side. */}
            <div className="login__controls">
              <LanguageSwitcher />
              <AppearanceMenu />
            </div>
          </div>
          {/* Same descriptor the shell shows, so signing in does not change
              what the product calls itself. Under the masthead, not in it: its
              length follows the language and must not push the switchers down. */}
          <p className="login__descriptor">{t('app.descriptor')}</p>
          <p className="login__subtitle">{t('login.subtitle')}</p>
        </header>

        <form className="login__form" onSubmit={onSubmit} noValidate>
          <h2 className="login__title">{t('login.title')}</h2>

          <label className="field">
            <span className="field__label">{t('login.username')}</span>
            <input
              className="field__input"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              {...(shownError ? { 'aria-invalid': true } : {})}
              aria-describedby={shownError ? 'login-error' : undefined}
            />
          </label>

          {/* Not a wrapping label: the eye inside it would be read into the
              password field's name. */}
          <div className="field">
            <label className="field__label" htmlFor="login-password">
              {t('login.password')}
            </label>
            <PasswordInput
              id="login-password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              {...(shownError ? { 'aria-invalid': true } : {})}
              aria-describedby={shownError ? 'login-error' : undefined}
            />
          </div>

          {shownError ? (
            <p id="login-error" className="field__error" role="alert">
              {t(shownError)}
            </p>
          ) : null}

          <button type="submit" className="button button--primary" disabled={signIn.isPending}>
            {signIn.isPending ? t('login.submitting') : t('login.submit')}
          </button>
        </form>

        {/* The card's last line, as the shell's foot is the workspace's: who
            built this, in the smallest type the product sets. Inside the card
            rather than under it, because the card IS the page here. */}
        <footer className="login__foot">
          <DeveloperCredit />
        </footer>
      </div>
    </main>
  )
}
